import { getCatalogueCardDate } from '../getCatalogueCardDate';
import type { MarkdownTokenList } from '$global/components/Markdown';
import { getImageUrl } from '$global/utils/getImageUrl';

// Structural, not the generated query type: this transform is fed from the
// page's `catalogue` on BOTH paths (the REST contract and the GraphQL
// document). Describing the fields the transform actually reads is what lets
// one implementation serve both.
export interface CatalogueCardLike {
	__typename?: string;
	id?: string | null;
	name?: string | null;
	slug?: string | null;
	code?: string | null;
	short_description?: string | null;
	description?: string | null;
	// The descriptions parsed by the server load
	// (withCatalogueDescriptionTokensServer).
	shortDescriptionTokens?: MarkdownTokenList;
	descriptionTokens?: MarkdownTokenList;
	release_date?: string | null;
	image?: {
		filename_disk?: string | null;
		width?: number | null;
		height?: number | null;
	} | null;
	collection_featured_image?: {
		filename_disk?: string | null;
		width?: number | null;
		height?: number | null;
	} | null;
	drop_page?: {
		drop_config?:
			| ({
					drop_start_date?: string | null;
					checkout_end_date?: string | null;
			  } | null)[]
			| null;
	} | null;
}

interface CardImage {
	url: string;
	width?: number | null;
	height?: number | null;
}

/**
 * A single Editions-section card. Every card represents a group of works: a
 * Series page for a series, and the edition's own page for an edition that
 * belongs to no series.
 */
export interface EditionsSectionCard {
	__typename: 'Series' | 'Editions';
	id: string;
	name: string;
	/** Markdown: tokens from the server load, or the text. */
	description: string | MarkdownTokenList;
	href: string;
	image: CardImage;
	// Used only for ordering; the card itself never renders it.
	releaseDate: string | null;
}

// Newest first. Entries without a release date sort last rather than jumping to
// the top, which is what a naive descending compare on null would do.
const byReleaseDateDesc = (a: EditionsSectionCard, b: EditionsSectionCard) => {
	if (a.releaseDate === b.releaseDate) return 0;
	if (!a.releaseDate) return 1;
	if (!b.releaseDate) return -1;
	return a.releaseDate < b.releaseDate ? 1 : -1;
};

/**
 * Builds the Editions section's cards from the artist's series and the editions
 * that belong to no series.
 *
 * A series is one card — it is never broken down into its editions. A
 * seriesless edition becomes its own card, described by its short_description
 * so it reads consistently beside the series cards. Both kinds are interleaved
 * newest-first, a series falling back to its drop config dates when it has no
 * release_date of its own.
 */
export const transformCatalogueEditions = (
	series: readonly (CatalogueCardLike | null)[] | undefined,
	serieslessEditions: readonly (CatalogueCardLike | null)[] | undefined = []
): EditionsSectionCard[] => {
	const seriesCards: EditionsSectionCard[] = (series || [])
		.filter((item) => item?.slug)
		.map((item) => ({
			__typename: 'Series' as const,
			id: item!.id ?? '',
			name: item!.name || '',
			description:
				item!.shortDescriptionTokens ?? (item!.short_description || ''),
			href: `/editions/series/${item!.slug}`,
			image: {
				url: getImageUrl(item!.image?.filename_disk ?? undefined),
				width: item!.image?.width ?? undefined,
				height: item!.image?.height ?? undefined,
			},
			releaseDate: getCatalogueCardDate(item!),
		}));

	const editionCards: EditionsSectionCard[] = (serieslessEditions || [])
		.filter((item) => item?.code)
		.map((item) => ({
			__typename: 'Editions' as const,
			id: item!.id ?? '',
			name: item!.name || '',
			description:
				item!.shortDescriptionTokens ?? (item!.short_description || ''),
			href: `/editions/artwork/${item!.code}`,
			image: {
				url: getImageUrl(item!.image?.filename_disk ?? undefined),
				width: item!.image?.width ?? undefined,
				height: item!.image?.height ?? undefined,
			},
			releaseDate: item!.release_date ?? null,
		}));

	return [...seriesCards, ...editionCards].sort(byReleaseDateDesc);
};
